import { useState, useEffect } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { WifiOffIcon, ChevronRightIcon } from '../Icons'
import { cx } from '@utils/cx'
import { getSessionItem, setSessionItem } from '../../../utils/storage'
import styles from './OfflineBanner.module.css'

const STORAGE_KEY = 'offlineBannerMinimized'

/**
 * OfflineBanner - Compact pill indicator for offline status
 *
 * Features:
 * - Collapsible: user can minimize to icon-only circle
 * - Non-blocking: positioned to avoid navigation overlap
 * - Responsive: top-left on mobile, top-center on tablet+
 * - Accessible: proper ARIA attributes, touch targets
 *
 * - Minimized state stored in sessionStorage (per session only)
 * - Resets on browser restart, not on route changes
 *
 * @note Rendered conditionally by ScreenWrapper when offline
 */

export function OfflineBanner () {
  const [isMinimized, setIsMinimized] = useState(() => {
    return getSessionItem(STORAGE_KEY) === 'true'
  })
  const { t } = useLanguage()

  useEffect(() => {
    setSessionItem(STORAGE_KEY, isMinimized)
  }, [isMinimized])

  const handleToggle = () => {
    setIsMinimized(prev => !prev)
  }

  return (
    <div
      className={cx(styles.banner, isMinimized && styles.isMinimized)}
      role='status'
      aria-live='polite'
    >
      {/* Decorative icon when expanded */}
      {!isMinimized && (
        <span className={styles.icon} aria-hidden='true'>
          <WifiOffIcon size={20} stroke={2} />
        </span>
      )}
      {/* Accessible button when minimized */}
      {isMinimized && (
        <button
          type='button'
          className={styles.iconButton}
          onClick={handleToggle}
          aria-label={t.core.offline.expand}
        >
          <WifiOffIcon size={20} stroke={2} />
        </button>
      )}

      <p className={styles.text}>
        {t.core.offline.message}
      </p>

      <button
        type='button'
        className={cx(
          styles.toggle,
          isMinimized ? styles.toggleCollapsed : styles.toggleExpanded
        )}
        onClick={handleToggle}
        aria-label={isMinimized ? t.core.offline.expand : t.core.offline.minimize}
        aria-expanded={!isMinimized}
        tabIndex={isMinimized ? -1 : 0}
      >
        <ChevronRightIcon size={18} stroke={2} />
      </button>
    </div>
  )
}
